import { forwardRef, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Checkbox, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

// third-party
import { Column, useExpanded, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';

// project import
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import useLocalStorage from '../../hooks/useLocalStorage';

// assets
import { CSVExport, HeaderSort, HidingSelect } from '../../components/third-party/ReactTable';
import { TablePagination } from '../../components/third-party/custom/ReactTableCustom';
import { EmptyTable } from '../../components/EmptyTable';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import './ExpandingConfigTable.css';
import { sizeByBreakpoint } from '../../utils/helper';

// ==============================|| GROUPED SUB ROW ||============================== //

function SubRows({ row, rowProps, data, isParentHovered, onChildHover }: any) {
  const theme = useTheme();

  if (!data || data.length === 0) return null;

  const childCount = data.length;
  const hoverBgColor = theme.palette.mode === 'dark' ? 'rgba(31, 32, 34, 0.2)' : 'rgba(229, 243, 241, 0.6)';

  const tableCellStyles = {
    paddingX: sizeByBreakpoint(1, 16),
    paddingY: sizeByBreakpoint(1, 12),
    fontSize: sizeByBreakpoint(1, 14),
    fontWeight: 600,
    whiteSpace: 'nowrap'
  };

  return (
    <>
      {data.map((child: any, i: number) => {
        const isFirst = i === 0;
        const isLast = i === childCount - 1;

        return (
          <TableRow
            key={`${rowProps.key}-child-${i}`}
            onMouseEnter={() => onChildHover(true)}
            onMouseLeave={() => onChildHover(false)}
            sx={{
              backgroundColor: isParentHovered ? `${hoverBgColor} !important` : theme.palette.background.default,
              transition: 'background-color 0.15s ease',
              '&:hover': {
                backgroundColor: `${hoverBgColor} !important`
              },
              // Close the gap with parent/previous row for ALL child rows
              position: 'relative',
              top: isFirst ? '-8px' : '-16px',
              marginBottom: isFirst ? '-8px' : '-16px'
            }}
          >
            {row.cells.map((cell: any, idx: number) => {
              const isFirstCell = idx === 0;
              const isLastCell = idx === row.cells.length - 1;

              return (
                <TableCell
                  key={idx}
                  sx={{
                    // Outer solid border (continuation from parent) - on ALL child rows
                    borderTop: 'none !important',
                    borderBottom: isLast ? '1px solid' : 'none !important',
                    borderLeft: isFirstCell ? '1px solid' : 'none !important',
                    borderRight: isLastCell ? '1px solid' : 'none !important',
                    borderColor: `${theme.palette.border.main} !important`,
                    borderBottomLeftRadius: isLast && isFirstCell ? '5px' : 0,
                    borderBottomRightRadius: isLast && isLastCell ? '5px' : 0,
                    // Padding for the dashed border spacing - only on outer edges
                    paddingTop: isFirst ? '12px !important' : '8px !important',
                    paddingBottom: isLast ? '12px !important' : '0 !important',
                    paddingLeft: isFirstCell ? '12px !important' : '0 !important',
                    paddingRight: isLastCell ? '12px !important' : '0 !important',
                    // Ensure cell stretches to full row height
                    height: '1px' // Trick to make cell stretch
                  }}
                >
                  {/* Content wrapper with background - extends to fill gaps and full height */}
                  <Box
                    sx={{
                      ...tableCellStyles,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? '#261C0B'
                        : alpha(theme.palette.warning[50] as string, 0.6),
                      paddingLeft: isFirstCell ? '12px' : '12px',
                      paddingRight: isLastCell ? '16px' : '8px',
                      // Full height to align borders
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      // Dashed border segments - top and bottom on ALL children
                      borderTop: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(214, 137, 16, 0.4)' : theme.palette.warning.main}`,
                      borderBottom: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(214, 137, 16, 0.4)' : theme.palette.warning.main}`,
                      borderLeft: isFirstCell ? `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(214, 137, 16, 0.4)' : theme.palette.warning.main}` : 'none',
                      borderRight: isLastCell ? `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(214, 137, 16, 0.4)' : theme.palette.warning.main}` : 'none',
                      // Border radius on all corners for each child row
                      borderTopLeftRadius: isFirstCell ? '5px' : 0,
                      borderTopRightRadius: isLastCell ? '5px' : 0,
                      borderBottomLeftRadius: isFirstCell ? '5px' : 0,
                      borderBottomRightRadius: isLastCell ? '5px' : 0,
                      // Extend horizontally to close gaps between cells
                      marginLeft: isFirstCell ? 0 : '-5px',
                      marginRight: isLastCell ? 0 : '-5px',
                      // Extend vertically to connect with adjacent rows
                      marginTop: isFirst ? 0 : '-1px',
                      // Add spacing between children (not after last)
                      marginBottom: isLast ? 0 : '5px',
                      ...(isLastCell && { whiteSpace: 'nowrap' })
                    }}
                  >
                    {cell.render(cell.column.SubCell || 'Cell', {
                      value: child[cell.column.id],
                      row: { ...row, original: child }
                    })}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </>
  );
}

// ==============================|| SUB ROW - ASYNC DATA ||============================== //

function SubRowAsync({ row, rowProps, children, isParentHovered, onChildHover }: any) {
  return <SubRows row={row} rowProps={rowProps} data={children} isParentHovered={isParentHovered} onChildHover={onChildHover} />;
}

// ==============================|| SELECTION - PREVIEW ||============================== //

export const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: { indeterminate: any }, ref: any) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  return <Checkbox indeterminate={indeterminate} ref={resolvedRef} {...rest} />;
});

// ==============================|| REACT TABLE ||============================== //

function ExpendingReactTable({
  columns,
  data,
  renderRowSubComponent,
  top = false,
  getHeaderProps,
  isLoading = true,
  isHideColumn = false,
  isCsvDownload = false,
  visibleColumns,
  returnVisibleItems,
  csvFileName = 'table-download.csv',
  expanded = false,
  lastTHWidth,
  onToggleExpand
}: any) {
  const theme = useTheme();
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [storedPageSize, setStoredPageSize] = useLocalStorage<number>('copierSettingsPageSize', 10);

  const handleMouseEnter = (rowIndex: number) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setHoveredRowIndex(rowIndex);
  };

  const handleMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredRowIndex(null);
    }, 30);
  };

  // Create initial expanded state - use expanded property from data rows (persisted in localStorage)
  const initialExpandedState = useMemo(() => {
    if (data) {
      const expandedState: Record<string, boolean> = {};
      data.forEach((row: any, index: number) => {
        // Use expanded property from row, default to true if not set
        expandedState[index.toString()] = row.expanded !== undefined ? row.expanded : true;
      });
      return expandedState;
    }
    return {};
  }, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // visibleColumns,
    // @ts-ignore
    gotoPage,
    // @ts-ignore
    setPageSize,
    setHiddenColumns,
    // @ts-ignore
    selectedFlatRows,
    allColumns,
    // @ts-ignore
    state: { pageIndex, pageSize, hiddenColumns }
  } = useTable(
    {
      columns: columns,
      data,
      // @ts-ignore
      initialState: {
        // @ts-ignore
        pageIndex: 0,
        pageSize: storedPageSize,
        expanded: initialExpandedState,
        hiddenColumns: columns
          .filter((col: Column<{}>) => visibleColumns?.includes(col.accessor as string))
          .map((col: any) => col.accessor) as string[]
      },
      expandSubRows: false,
      autoResetExpanded: false
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  // No longer auto-expand all rows - use persisted state from data
  // This effect is removed to respect individual row expanded states

  // Wrapper for gotoPage to handle 'next'/'prev' strings
  const handleGotoPage = useCallback((value: 'next' | 'prev' | number) => {
    if (value === 'next') {
      gotoPage(pageIndex + 1);
    } else if (value === 'prev') {
      gotoPage(pageIndex - 1);
    } else {
      gotoPage(value);
    }
  }, [gotoPage, pageIndex]);

  // Handle page size change and persist to localStorage
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setStoredPageSize(size);
  }, [setPageSize, setStoredPageSize]);

  console.log('selectedFlatRows: ', selectedFlatRows);

  const sortingRow = rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  let headers: LabelKeyObject[] = [];
  allColumns.map((item) => {
    if (!hiddenColumns?.includes(item.id)) {
      headers.push({ label: item.Header as string, key: item.id });
    }
    return item;
  });

  // ============================== Styles ============================== //
  const tableHeaderStyles = {
    paddingX: sizeByBreakpoint(1, 16),
    paddingY: 0,
    fontSize: sizeByBreakpoint(1, 14),
    fontWeight: 500,
    color: '#6B7580',
    lineHeight: '20px',
    border: 'none !important',
    backgroundColor: 'transparent !important',
    textTransform: 'capitalize',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' },
    whiteSpace: 'nowrap'
  };

  const tableCellStyles = {
    paddingX: sizeByBreakpoint(1, 16),
    paddingY: sizeByBreakpoint(1, 12),
    fontSize: sizeByBreakpoint(1, 14),
    fontWeight: 600,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: `${theme.palette.border.main} !important`,
    whiteSpace: 'nowrap'
  };

  return (
    <Stack>
      {top && (
        <Box sx={{ p: 2 }}>
          {/*<TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageIndex={pageIndex} pageSize={pageSize} />*/}
        </Box>
      )}
      {isHideColumn && (
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ p: 2 }}>
          <HidingSelect
            hiddenColumns={hiddenColumns!}
            setHiddenColumns={setHiddenColumns}
            allColumns={allColumns}
            returnVisibleItems={(items: any[]) => {
              if (returnVisibleItems) {
                returnVisibleItems(items);
              }
            }}
          />
          {isCsvDownload && <CSVExport data={data} filename={csvFileName} headers={headers} />}
        </Stack>
      )}
      <Table {...getTableProps()} sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
        <TableHead sx={{ backgroundColor: 'transparent !important' }}>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ backgroundColor: 'transparent !important' }}>
              {headerGroup.headers.map((column: any, idx) => (
                <TableCell
                  {...column.getHeaderProps([{ className: column.className }, getHeaderProps(column)])}
                  sx={{
                    ...tableHeaderStyles,
                    ...(idx === headerGroup.headers.length - 1 && {
                      pr: '24px !important',
                      whiteSpace: 'nowrap',
                      width: lastTHWidth ?? '1%'
                    })
                  }}
                >
                  <HeaderSort column={{ ...column, disableSortBy: true }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()} className="custom-hover">
          {sortingRow?.length > 0 || isLoading ? (
            sortingRow.map((row: any, i: number) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              const hasChildren = row.original.children && row.original.children.length > 0;
              const isExpanded = row.isExpanded && hasChildren;
              const isRowHovered = hoveredRowIndex === i;
              const hoverBgColor = theme.palette.mode === 'dark' ? 'rgba(31, 32, 34, 0.2)' : 'rgba(229, 243, 241, 0.6)';
              return (
                <Fragment key={i}>
                  <TableRow
                    {...row.getRowProps()}
                    onMouseEnter={() => handleMouseEnter(i)}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      backgroundColor: isRowHovered ? `${hoverBgColor} !important` : theme.palette.background.default,
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${hoverBgColor} !important`
                      }
                    }}
                  >
                    {row.cells.map((cell: any, idx: number) => {
                      return (
                        <TableCell
                          key={idx}
                          {...cell.getCellProps([{ className: cell.column.className }])}
                          sx={{
                            ...tableCellStyles,
                            ...(isExpanded && {
                              borderBottom: 'none !important'
                            }),
                            ...(idx === 0 && {
                              pl: '16px',
                              borderLeft: '1px solid',
                              borderTopLeftRadius: '5px',
                              borderBottomLeftRadius: isExpanded ? '0px' : '5px'
                            }),
                            ...(idx === row.cells.length - 1 && {
                              pr: '24px',
                              whiteSpace: 'nowrap',
                              borderRight: '1px solid',
                              borderTopRightRadius: '5px',
                              borderBottomRightRadius: isExpanded ? '0px' : '5px'
                            })
                          }}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {row.isExpanded && (
                    <SubRowAsync 
                      row={row} 
                      rowProps={rowProps} 
                      children={row.original.children || []} 
                      isParentHovered={isRowHovered}
                      onChildHover={(isHovered: boolean) => isHovered ? handleMouseEnter(i) : handleMouseLeave()}
                    />
                  )}
                </Fragment>
              );
            })
          ) : (
            <EmptyTable msg={'No Data'} colSpan={columns?.length} />
          )}

          {/*{rows.map((row: any, i: number) => {
            prepareRow(row);
            const rowProps = row.getRowProps();

            return (
              <Fragment key={i}>
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell: any) => (
                    <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>{cell.render('Cell')}</TableCell>
                  ))}
                </TableRow>
                {row.isExpanded && renderRowSubComponent({ row, rowProps, visibleColumns })}
              </Fragment>
            );
          })}*/}

          {!top && (
            <TableRow
              sx={{
                backgroundColor: 'transparent !important',
                '& td': {
                  border: 'none !important',
                  backgroundColor: 'transparent !important',
                  p: '0 !important',
                  pt: '16px !important'
                }
              }}
            >
              <TableCell sx={{ p: 2 }} colSpan={columns?.length}>
                <TablePagination
                  handlePageSize={handlePageSizeChange}
                  gotoPage={handleGotoPage}
                  rows={rows}
                  setPageSize={handlePageSizeChange}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  dataCount={data?.length ?? 0}
                />
                {/*<TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageIndex={pageIndex} pageSize={pageSize} />*/}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  );
}

// ==============================|| REACT TABLE - EXPANDING TABLE ||============================== //

const ExpandingConfigTable = ({
  data,
  allColumns,
  isLoading,
  isHideColumn = false,
  visibleColumns,
  returnVisibleItems,
  isCsvDownload = false,
  csvFileName = 'table-download.csv',
  expanded = false,
  onToggleExpand
}: {
  data: any;
  allColumns: any;
  isLoading: boolean;
  isHideColumn?: boolean;
  visibleColumns?: any[];
  returnVisibleItems?: (items: any[]) => void;
  isCsvDownload?: boolean;
  csvFileName?: string;
  expanded?: boolean;
  onToggleExpand?: (masterId: string, isExpanded: boolean) => void;
}) => {
  const theme = useTheme();
  const columns = useMemo(
    () => allColumns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme]
  );
  useEffect(() => {
    console.log('ExpandingConfigTable DATA: ', data);
  }, [data]);

  const renderRowSubComponent = useCallback(({ row, rowProps, isParentHovered, onChildHover }) => {
    console.log(row);
    return <SubRowAsync row={row} rowProps={rowProps} children={row?.original?.children || []} isParentHovered={isParentHovered} onChildHover={onChildHover} />;
  }, []);

  return (
    <Grid item xs={12}>
      <MainCard content={false} border={false}>
        <ScrollX>
          <ExpendingReactTable
            isLoading={isLoading}
            columns={columns}
            data={data}
            renderRowSubComponent={renderRowSubComponent}
            getHeaderProps={(column: any) => column.getSortByToggleProps()}
            isHideColumn={isHideColumn}
            visibleColumns={visibleColumns}
            returnVisibleItems={returnVisibleItems}
            csvFileName={csvFileName}
            isCsvDownload={isCsvDownload}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
          />
        </ScrollX>
      </MainCard>
    </Grid>
  );
};

export default ExpandingConfigTable;
